import {useCallback, useEffect, useMemo, useRef} from 'react'
import {getState, updateForm, useAutoForm, useSelector} from '../../../store.js'
import {
  useZropdown,
  useZropdownElement,
} from '../../../common/components/Zropdown.js'
import {currentDropdownSelector} from '../../../redux/selectors/ui/index.js'
import FilterDropdown from '../../../common/components/FilterDropdown.js'
import {
  isLabelPropertyEnabledSelector,
  labelConfigSelector,
  labelShippersSelector,
  onLabelPropertyValueChange,
  shipperTypesSelector,
  updateLabelConfig,
} from '../../../data/labelInfos/index.js'
import {
  filterOptions,
  getNounsFromValue,
  tokenizeOptions,
} from '../../../common/tokenizeOptions.js'
import className from '../../../common/className.js'
import Checkbox from '../../../common/components/Checkbox.js'
import useLabelProperty, {
  SHIPPER_DEPENDENT_PROPERTIES_FUNC,
  formatMetaClassName,
} from './useLabelProperty.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {
  Count,
  IfPluralNotZero,
  IfSingle,
  IfZero,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'
import {
  SHIPPER_NAMES,
  SHIPPER_VENDORS,
} from '../../../common/constants/ShipperNames.js'
import useStable from '../../../common/useStable.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import {shipperShippingMethodOptionsSelector} from '../../../data/shippers.js'
import {usePresetMultiShipperSelector} from '../../../data/company.js'
import OverrideLabelProperty from './OverrideLabelProperty.js'

const SHIPPING_METHOD = 'SHIPPING_METHOD'
const SHIPPING_METHOD__SHIPPER_HEADER = 'SHIPPING_METHOD__SHIPPER_HEADER'
const LABEL_PROPERTY_SHIPPING_METHODS = 'preferred_shipping_methods'

export function formatPreferredShippingMethodsProperty(shipperType) {
  return `${shipperType}__preferred_shipping_methods`
}

export function shippingMethodLabelPropertiesFunc(shipperType, labelInfoID) {
  const shipperTypes = shipperTypesSelector(getState(), {labelInfoID})
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  return [
    LABEL_PROPERTY_SHIPPING_METHODS,
    ...shipperTypes
      .map(formatPreferredShippingMethodsProperty)
      .filter((labelProperty) => (labelConfig[labelProperty] || []).length > 0),
  ]
}

export const VALID_SHIPPING_METHOD_LABEL_PROPERTIES = [
  LABEL_PROPERTY_SHIPPING_METHODS,
  ...SHIPPER_VENDORS.map(formatPreferredShippingMethodsProperty),
]

function enforceSingleShipperShippingMethod(labelInfoID, onChange) {
  const shippers = labelShippersSelector(getState(), {labelInfoID})

  if (shippers.length === 1) {
    const shipper = shippers[0]
    const labelProperty = formatPreferredShippingMethodsProperty(shipper.vendor)
    const labelConfig = labelConfigSelector(getState(), {labelInfoID})
    const shippingMethod = labelConfig[labelProperty]

    if (!shippingMethod) {
      const options = shipperShippingMethodOptionsSelector(getState(), {
        shipperID: shipper.id,
      })
      updateLabelConfig(labelInfoID, {[labelProperty]: [options[0].value]})

      onLabelPropertyValueChange(labelInfoID, labelProperty)

      if (onChange) {
        onChange()
      }
    }
  }
}

function selectedShippingMethodOptionsSummarySelector(state, {labelInfoID}) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})
  const shippers = labelShippersSelector(state, {labelInfoID})

  const selectedOptionsByType = {}
  let selectedCount = 0
  let firstSelectedDisplay = ''

  for (const shipper of shippers) {
    const shippingMethods =
      labelConfig[formatPreferredShippingMethodsProperty(shipper.vendor)] || []

    const options = shipperShippingMethodOptionsSelector(state, {
      shipperID: shipper.id,
    })

    const selectedOptions = (options || []).filter(({value}) =>
      shippingMethods.includes(value),
    )

    if (selectedOptions.length) {
      selectedOptionsByType[shipper.vendor] = selectedOptions

      selectedCount += selectedOptions.length

      if (!firstSelectedDisplay) {
        firstSelectedDisplay = selectedOptions[0].display
      }
    }
  }

  return {
    selectedOptionsByType,
    selectedCount,
    firstSelectedDisplay,
  }
}

function filterShippingMethod(
  dropdown,
  shipperOptions,
  shipperOptionTokens,
  text,
) {
  try {
    if (!text) {
      updateForm(dropdown, {
        results: shipperOptions,
        hasFoundNothing: false,
      })

      return
    }

    const results = filterOptions(text, shipperOptionTokens)

    updateForm(dropdown, {
      results,
      selectedIndex: 0,
      hasFoundNothing: results.length === 0,
    })
  } catch (err) {
    updateForm(dropdown, {
      serverError: err.message,
    })
  }
}

function onShippingMethodSelected(labelInfoID, onChange, option) {
  const labelProperty = formatPreferredShippingMethodsProperty(
    option.shipperType,
  )
  const {[labelProperty]: existingMethods = []} = labelConfigSelector(
    getState(),
    {
      labelInfoID,
    },
  )

  const shippingMethods = existingMethods.filter(
    (value) => option.value !== value,
  )

  if (shippingMethods.length === existingMethods.length) {
    shippingMethods.push(option.value)
  }

  updateLabelConfig(labelInfoID, {
    [labelProperty]: shippingMethods,
  })

  onLabelPropertyValueChange(labelInfoID, labelProperty)

  if (onChange) {
    onChange()
  }
}

function onShipperSelected(
  labelInfoID,
  onChange,
  option,
  shippingMethodOptions,
) {
  const shipperType = option.value
  const labelProperty = formatPreferredShippingMethodsProperty(shipperType)
  const {[labelProperty]: existingMethods = []} = labelConfigSelector(
    getState(),
    {
      labelInfoID,
    },
  )

  const options = shippingMethodOptions.filter(
    (o) => o.shipperType === shipperType,
  )

  const shippingMethods =
    existingMethods.length === options.length
      ? []
      : options.map(({value}) => value)

  updateLabelConfig(labelInfoID, {
    [labelProperty]: shippingMethods,
  })

  onLabelPropertyValueChange(labelInfoID, labelProperty)

  if (onChange) {
    onChange()
  }
}

function Item({option, selected, disabled, isFocused, onSelect}) {
  const id = `${option.shipperType}__preferred_shipping_method__${option.value}`
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li ref={ref} className="list__item list__item--dropdown">
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap label--selectable-left-indent margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
          disabled,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset margin-bottom-0"
          htmlFor={id}
          data-dropdown-prevent-close
        >
          <span className="margin-right-5">
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={id}
              checked={selected}
              onClick={() => {
                if (!disabled) {
                  onSelect(option)
                }
              }}
            />
          </span>
          <span className="label--selectable__text">{option.display}</span>
        </label>
      </span>
    </li>
  )
}

function ItemSeparator({option, selected, indeterminate, isFocused, onSelect}) {
  const ref = useRef()

  useZropdownElement({ref, isSelected: isFocused})

  return (
    <li
      ref={ref}
      className="list__item list__item--dropdown list__item--dropdown-header list__item--dropdown-header-pkg-type"
    >
      <span
        className={className`label--selectable label--selectable-pt-pb-0 flex--justify-nowrap margin-bottom-0 ${{
          'list__link--dropdown-arrow-focus': isFocused,
        }}`}
      >
        <label
          className="flex--justify-nowrap flex-grow flex--jc-left mw-unset align-items-center  margin-bottom-0"
          htmlFor={option.value}
          data-dropdown-prevent-close
        >
          <span>
            <Checkbox
              mode="fancy"
              className="fs-00 v-align-middle"
              id={option.value}
              checked={selected}
              indeterminate={indeterminate}
              onClick={() => onSelect(option)}
            />
          </span>
          <span
            className={className`label--selectable__text label__carrier-logo lh-sm ${option.value}`}
          >
            Shipping Methods
          </span>
        </label>
      </span>
    </li>
  )
}

export function getValidShippingMethodOptions(labelInfoID) {
  const shippers = labelShippersSelector(getState(), {labelInfoID})
  const commonOptionsByShipperType = {}
  const options = []

  for (const shipper of shippers) {
    const shipperType = shipper.vendor
    const shippingMethodOptions = shipperShippingMethodOptionsSelector(
      getState(),
      {
        shipperID: shipper.id,
      },
    ).filter(
      (option) =>
        !(shipper?.hidden_shipping_methods || []).includes(option.value),
    )

    if (shippingMethodOptions.length === 0) {
      continue
    }

    const commonOptions = commonOptionsByShipperType[shipperType]

    if (commonOptions) {
      commonOptionsByShipperType[shipperType] = commonOptions.filter((option) =>
        shippingMethodOptions.find(({value}) => value === option.value),
      )
    } else {
      commonOptionsByShipperType[shipperType] = shippingMethodOptions
    }
  }

  for (const [shipperType, commonOptions] of Object.entries(
    commonOptionsByShipperType,
  )) {
    const theseOptionsMap = new Map()
    for (const option of commonOptions) {
      theseOptionsMap.set(option.value, {
        value: option.value,
        display: option.display,
        entity: option,
        shipperType: shipperType,
        type: SHIPPING_METHOD,
        nouns: [
          ...getNounsFromValue(option.display),
          shipperType,
          ...(option.carrier ? [option.carrier] : []),
        ],
      })
    }

    const theseOptions = [...theseOptionsMap.values()]
    options.push({
      value: shipperType,
      display: SHIPPER_NAMES[shipperType],
      entity: theseOptions.map(({entity}) => entity),
      type: SHIPPING_METHOD__SHIPPER_HEADER,
      nouns: new Set(
        theseOptions.reduce((prev, {nouns}) => {
          prev.push(...nouns)
          return prev
        }, []),
      ),
    })

    options.push(...theseOptions)
  }

  return options
}

function enforceValidPreferredShippingMethodsSelection(
  labelInfoID,
  onChange,
  shippingMethodOptions,
) {
  const shipperTypes = shipperTypesSelector(getState(), {labelInfoID})
  const labelConfig = labelConfigSelector(getState(), {
    labelInfoID,
  })

  for (const shipperType of shipperTypes) {
    const labelProperty = formatPreferredShippingMethodsProperty(shipperType)
    const existingList = labelConfig[labelProperty] || []
    const options = shippingMethodOptions.filter(
      (option) =>
        option.shipperType === shipperType && option.type === SHIPPING_METHOD,
    )

    const newList = existingList.filter((shippingMethod) =>
      options.find(({value}) => value === shippingMethod),
    )

    if (newList.length === existingList.length) {
      continue
    }

    updateLabelConfig(labelInfoID, {
      [labelProperty]: newList,
    })

    onLabelPropertyValueChange(labelInfoID, labelProperty)

    if (onChange) {
      onChange()
    }
  }
}

/**
 * Shipping Method filter for label info conf
 *
 * @param {Object} props
 * @param {Boolean} props.autoFocus
 * @returns {React.ReactNode}
 */
export default function ShippingMethodFilter({autoFocus}) {
  const {contextID, labelInfoID, onChange, isSummary, isPickMode} =
    useLabelConfigContext()
  const propertySpecs = ShippingMethodFilter.propertySpecList[0]
  const [labelProperty, firstRealShippingMethodLabelProperty] =
    propertySpecs.labelPropertiesFunc(null, labelInfoID)
  const dropdown = `SHIPPING_METHOD_FILTER_${contextID}_${labelInfoID}`
  const usePresetMultiShipper = useSelector(usePresetMultiShipperSelector)

  const form = useAutoForm(dropdown, {
    text: '',
    results: [],
    selectedIndex: 0,
    hasFoundNothing: false,
    serverError: null,
    hasChanged: false,
  })
  const localOnChange = useCallback(() => {
    updateForm(dropdown, {hasChanged: true})
  }, [])

  const shippers = useStable(
    useSelector((state) => labelShippersSelector(state, {labelInfoID})),
  )
  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty,
    }),
  )

  const shippingMethodOptions = useStable(
    useMemo(
      () => getValidShippingMethodOptions(labelInfoID),
      [labelInfoID, shippers],
    ),
  )
  const shippingMethodOptionTokens = useMemo(
    () => tokenizeOptions(shippingMethodOptions),
    [shippingMethodOptions],
  )

  const {selectedOptionsByType, selectedCount, firstSelectedDisplay} =
    useSelector((state) =>
      selectedShippingMethodOptionsSummarySelector(state, {labelInfoID}),
    )

  useEffect(() => {
    filterShippingMethod(
      dropdown,
      shippingMethodOptions,
      shippingMethodOptionTokens,
      form && form.text,
    )
  }, [shippingMethodOptions, form && form.text])

  const buttonRef = useRef()

  const {onChangeInternal, onSelectInternal, onConfirm, onUp, onDown} =
    useZropdown({
      dropdown,
      form,
      onSelect: (option) => {
        if (option.type === SHIPPING_METHOD) {
          onShippingMethodSelected(labelInfoID, localOnChange, option)
        } else {
          onShipperSelected(
            labelInfoID,
            localOnChange,
            option,
            shippingMethodOptions,
          )
        }
      },
      autoFocus,
    })

  const currentDropdown = useSelector(currentDropdownSelector)

  useEffect(() => {
    if (!currentDropdown && form.hasChanged) {
      updateForm(dropdown, {hasChanged: false})

      if (onChange) {
        onChange()
      }
    }
  }, [currentDropdown])

  useLabelProperty({
    labelProperty,
    propertySpecs,
  })

  useEffect(() => {
    if (isPickMode && !firstRealShippingMethodLabelProperty) {
      enforceSingleShipperShippingMethod(labelInfoID, onChange)
    }
  }, [isPickMode, firstRealShippingMethodLabelProperty])

  useEffect(() => {
    enforceValidPreferredShippingMethodsSelection(
      labelInfoID,
      onChange,
      shippingMethodOptions,
    )
  }, [shippingMethodOptions])

  if (!usePresetMultiShipper) {
    return null
  }

  const label = 'Preferred Shipping Methods'

  if (isSummary) {
    return label
  }

  if (isPickMode && !isLabelPropertyEnabled) {
    return <PickLabelProperty label={label} labelProperty={labelProperty} />
  }

  return (
    <li
      className={className`list__item--shipping-options list__item--shipping-options-rs flex-grow ${formatMetaClassName(
        labelProperty,
      )}`}
    >
      <div className="wrap--edit-preset-form-input">
        <label className="label--bold" htmlFor={dropdown}>
          <span>{label}</span>
        </label>
        <div
          className={`wrap-outer--tag-dropdown-with-filter ${
            currentDropdown === dropdown ? 'open' : ''
          }`}
          data-dropdown={dropdown}
        >
          <button
            id={dropdown}
            ref={buttonRef}
            className="btn btn--dropdown btn--filter btn--filter-w-border w-100"
            type="button"
            disabled={shippingMethodOptions.length === 0}
          >
            <span className="btn__text flex">
              <span className="btn__text--no-overflow txt-overflow-ellipsis">
                <span className="txt-overflow-ellipsis">
                  <span>
                    <PluralBlock count={selectedCount}>
                      <IfZero>
                        <em>Select a shipping method...</em>
                      </IfZero>
                      <IfSingle>{firstSelectedDisplay}</IfSingle>
                      <IfPluralNotZero>
                        <Count /> <Plural word="shipping method" />
                      </IfPluralNotZero>
                    </PluralBlock>
                  </span>
                </span>
              </span>
            </span>
          </button>
          <FilterDropdown
            dropdown={dropdown}
            form={form}
            onChangeInternal={onChangeInternal}
            onConfirm={onConfirm}
            onUp={onUp}
            onDown={onDown}
            placeholder="Search shipping methods"
            includeSearchInput
            parentRef={buttonRef}
          >
            {form.results.map((option, index) => {
              const selectedOptions =
                selectedOptionsByType[
                  option.type === SHIPPING_METHOD
                    ? option.shipperType
                    : option.value
                ] || []
              const shipperSelectedCount = selectedOptions.length || 0

              return option.type === SHIPPING_METHOD ? (
                <Item
                  key={`${option.shipperType}__${option.value}`}
                  option={option}
                  selected={
                    !!selectedOptions.find(({value}) => value === option.value)
                  }
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              ) : (
                <ItemSeparator
                  key={option.value}
                  option={option}
                  selected={shipperSelectedCount > 0}
                  indeterminate={
                    option.entity.length !== shipperSelectedCount &&
                    shipperSelectedCount !== 0
                  }
                  isFocused={index === form.selectedIndex}
                  onSelect={onSelectInternal}
                />
              )
            })}
            {form.hasFoundNothing && (
              <li className="list__item--dropdown list__item--dropdown-empty align-center">
                No shipping method matches your search.
              </li>
            )}
          </FilterDropdown>
        </div>
      </div>
      <EnableLabelProperty labelProperty={labelProperty} />
      <OverrideLabelProperty
        propertySpecs={propertySpecs}
        label="Override other selected preferred shipping methods when preset is applied"
        helpLink="https://support.ordoro.com/how-do-additive-vs-overwrite-presets-work-in-rate-shopping/"
        helpTitle="Learn More"
      />
    </li>
  )
}

ShippingMethodFilter.propertySpecList = [
  {
    validShipperTypes: SHIPPER_VENDORS,
    labelPropertiesFunc: shippingMethodLabelPropertiesFunc,
    dependentPropertiesFunc: SHIPPER_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: () => VALID_SHIPPING_METHOD_LABEL_PROPERTIES,
  },
]
